import React from 'react';
import { PlusIcon } from '@heroicons/react/outline';
import Button from './Button';
import { cn } from './cn';

/**
 * Bun kanban primitives. Spec: Pipeline canvas. Columns keep their rainbow
 * tints deliberately (brighter than the status quintet — the bun-stage-*
 * tokens exist just for them); the cards inside stay calm surface white.
 *
 *   <StageColumn tone="red" title="Leads" count={200} onAdd={...}>
 *     <StageCard title="Jordan & Casey Liu" sub="John Martinez"
 *       actions={<Button size="sm" variant="secondary" iconOnly>...</Button>}
 *       footer={<span className="text-bun-label font-book text-bun-faint">27 days</span>} />
 *   </StageColumn>
 *
 * Stage-specific footer content (touch dots, overdue meta, progress bars)
 * is composed by the screen — the card only lays it out.
 */
const TONES = {
  red: {
    frame: 'border-bun-stage-red-border bg-bun-stage-red-bg',
    divider: 'border-bun-stage-red-border',
    text: 'text-bun-stage-red-text',
  },
  orange: {
    frame: 'border-bun-stage-orange-border bg-bun-stage-orange-bg',
    divider: 'border-bun-stage-orange-border',
    text: 'text-bun-stage-orange-text',
  },
  yellow: {
    frame: 'border-bun-stage-yellow-border bg-bun-stage-yellow-bg',
    divider: 'border-bun-stage-yellow-border',
    text: 'text-bun-stage-yellow-text',
  },
  blue: {
    frame: 'border-bun-stage-blue-border bg-bun-stage-blue-bg',
    divider: 'border-bun-stage-blue-border',
    text: 'text-bun-stage-blue-text',
  },
  amber: {
    frame: 'border-bun-stage-amber-border bg-bun-stage-amber-bg',
    divider: 'border-bun-stage-amber-border',
    text: 'text-bun-stage-amber-text',
  },
  green: {
    frame: 'border-bun-stage-green-border bg-bun-stage-green-bg',
    divider: 'border-bun-stage-green-border',
    text: 'text-bun-stage-green-text',
  },
  purple: {
    frame: 'border-bun-stage-purple-border bg-bun-stage-purple-bg',
    divider: 'border-bun-stage-purple-border',
    text: 'text-bun-stage-purple-text',
  },
  neutral: {
    frame: 'border-bun-line bg-bun-mist',
    divider: 'border-bun-line',
    text: 'text-bun-text',
  },
};

export default function StageColumn({
  tone = 'neutral',
  title,
  count,
  onAdd,
  className,
  unbounded = false,
  headingId,
  description,
  children,
  ...props
}) {
  const t = TONES[tone] || TONES.neutral;
  const sectionProps = { ...props };
  delete sectionProps.headerClassName;
  delete sectionProps.bodyClassName;
  const descriptionId = description && headingId ? `${headingId}-description` : undefined;
  return (
    <section
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      className={cn('relative border rounded-bun-lg overflow-hidden font-instrument', t.frame, className)}
      {...sectionProps}
    >
      <header
        className={cn(
          'px-[14px] py-3 flex items-center gap-2 border-b',
          t.divider
        )}
      >
        <h2
          id={headingId}
          className={cn(
            'm-0 font-instrument text-bun-body font-semibold leading-snug tracking-normal',
            t.text
          )}
        >
          {title}
        </h2>
        {count != null && (
          <span
            className={cn(
              'text-bun-label tracking-normal font-semibold bg-bun-stage-pill rounded-full px-[7px] py-px',
              t.text
            )}
          >
            {count}
          </span>
        )}
        <div className="flex-1" />
        {onAdd && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Add to ${title}`}
            onClick={onAdd}
          >
            <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
      </header>
      {description && (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      )}
      <div
        className={cn(
          'p-[10px] flex flex-col gap-2 overflow-y-auto',
          !unbounded && 'max-h-[560px]'
        )}
      >
        {children}
      </div>
    </section>
  );
}

export function StageCard({
  title,
  sub,
  actions,
  footer,
  onClick,
  className,
  children,
  as: Component = 'div',
  ...props
}) {
  const isNativeButton = Component === 'button';
  const interactiveProps = onClick
    ? isNativeButton
      ? { onClick, type: props.type || 'button' }
      : {
          onClick,
          role: 'button',
          tabIndex: 0,
          onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick(event);
            }
          },
        }
    : {};

  return (
    <Component
      {...props}
      {...interactiveProps}
      className={cn(
        'bg-bun-surface border border-bun-line rounded-bun px-3 py-[11px] font-instrument',
        onClick && 'cursor-pointer hover:border-bun-line-strong hover:shadow-bun-card',
        onClick &&
          'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue',
        className
      )}
    >
      {children ?? (
        <>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-bun-body font-strong text-bun-text truncate">{title}</div>
              {sub && (
                <div className="text-bun-meta font-book text-bun-stone mt-px truncate">{sub}</div>
              )}
            </div>
            {actions}
          </div>
          {footer && <div className="flex items-center justify-between gap-2 mt-2">{footer}</div>}
        </>
      )}
    </Component>
  );
}
